/**
 * «Мой день» — рабочий экран няни.
 *
 * Один экран на весь день: кнопка смены, плитки событий в один тап, расход,
 * «я на месте» и лента того, что уже отмечено. Ничего из этого не должно
 * требовать второго экрана или подтверждения — няня работает одной рукой.
 */

import { useState } from 'react'

import { useCurrency, useFamily, useSession, useTimeZone } from '@/app/session'
import { EventTiles, ActionTile } from '@/features/events/EventTiles'
import { ExpenseSheet } from '@/features/expenses/ExpenseSheet'
import { GeoConsentSheet } from '@/features/geo/GeoConsentSheet'
import { MapSheet } from '@/features/geo/MapSheet'
import { useGeoCapture } from '@/features/geo/useGeoCapture'
import { PhotoViewer } from '@/features/media/PhotoViewer'
import { MorningPlan } from '@/features/schedule/MorningPlan'
import { SkipSheet } from '@/features/schedule/SkipSheet'
import { TaskList } from '@/features/schedule/TaskList'
import { NannyShiftCard } from '@/features/shift/ShiftCard'
import { DaySummaryCard } from '@/features/trust/TrustPanel'
import { Timeline } from '@/features/timeline/Timeline'
import { formatLongDay } from '@/shared/format'
import type { ScheduleTask } from '@/shared/api/schedule'
import {
  useCreateEvent,
  useDay,
  useDeleteEvent,
  useEventTypes,
  useFinishShift,
  useGeoStatus,
  useHere,
  useStartShift,
  useStopInterval,
  type EventType,
  type GeoPoint,
} from '@/shared/api/day'
import { useCompleteTask, useDayPlan } from '@/shared/api/schedule'
import { useDaySummary } from '@/shared/api/trust'
import { Button } from '@/shared/ui/Button'
import { Screen } from '@/shared/ui/Screen'
import { ConfirmSheet } from '@/shared/ui/Sheet'
import { Card, SectionTitle } from '@/shared/ui/primitives'
import { ErrorState, SkeletonCard } from '@/shared/ui/states'
import { toast } from '@/shared/ui/toast'

export function MyDay() {
  const me = useSession((state) => state.me)
  const family = useFamily()
  const timeZone = useTimeZone()
  const currency = useCurrency()
  const familyId = family?.id ?? 0

  const [expenseOpen, setExpenseOpen] = useState(false)
  const [consentOpen, setConsentOpen] = useState(false)
  const [finishOpen, setFinishOpen] = useState(false)
  const [mapGeo, setMapGeo] = useState<GeoPoint | null>(null)
  const [photos, setPhotos] = useState<{ urls: string[]; index: number } | null>(null)
  const [busyTypeId, setBusyTypeId] = useState<number | null>(null)
  const [skipping, setSkipping] = useState<ScheduleTask | null>(null)

  const day = useDay(familyId, undefined, true)
  const types = useEventTypes(familyId)
  const plan = useDayPlan(familyId)
  const summary = useDaySummary(familyId)
  const geoStatus = useGeoStatus(familyId, Boolean(family?.settings?.geo_enabled))

  const geoActive = day.data?.geo_active ?? false
  const captureGeo = useGeoCapture(geoActive)

  const startShift = useStartShift(familyId, {
    onSuccess: () => toast.success('Смена началась'),
    onError: (error) => toast.error(error.message),
  })
  const finishShift = useFinishShift(familyId, {
    onSuccess: () => {
      setFinishOpen(false)
      toast.success('Смена закрыта')
    },
    onError: (error) => toast.error(error.message),
  })
  const deleteEvent = useDeleteEvent(familyId)
  const createEvent = useCreateEvent(familyId, {
    onSuccess: (event) => {
      // Undo вместо подтверждения: промахнуться в один тап легко,
      // а лишний диалог убил бы скорость.
      // Значок в тост не идёт: там строка, а не разметка, и тон тоста
      // уже показан своей иконкой.
      toast.undoable(event.title, () => deleteEvent.mutate(event.id))
    },
    onError: (error) => toast.error(error.message),
    onSettled: () => setBusyTypeId(null),
  })
  const stopInterval = useStopInterval(familyId, {
    onError: (error) => toast.error(error.message),
  })
  const completeTask = useCompleteTask(familyId, {
    onSuccess: (task) => toast.success(`${task.title} — отмечено`),
    onError: (error) => toast.error(error.message),
  })
  const here = useHere(familyId, {
    onSuccess: () => toast.success('Родители видят, что вы на месте'),
    onError: (error) => toast.error(error.message),
  })

  if (!me || !family) return null
  if (day.isLoading || types.isLoading) {
    return (
      <Screen title="Мой день">
        <SkeletonCard lines={2} />
        <SkeletonCard lines={4} />
      </Screen>
    )
  }
  if (day.error) {
    return (
      <Screen title="Мой день">
        <ErrorState error={day.error} onRetry={() => void day.refetch()} />
      </Screen>
    )
  }

  const data = day.data!
  const activeShift = data.active_shift
  const needsConsent =
    (family.settings?.geo_enabled ?? false) && geoStatus.data?.consent_granted === false

  const onCreate = async (type: EventType) => {
    setBusyTypeId(type.id)
    // точка снимается строго здесь, внутри обработчика нажатия
    const geo = type.capture_geo ? await captureGeo() : undefined
    createEvent.mutate({ type_id: type.id, geo })
  }

  return (
    <Screen title="Мой день" subtitle={formatLongDay(new Date(), timeZone)}>
      {needsConsent && (
        <Card className="bg-accent-soft">
          <p className="text-body font-semibold">Геометки включены родителями</p>
          <p className="text-text-muted mt-1 text-caption leading-snug">
            Решаете вы. Точка фиксируется только когда вы сами нажимаете кнопку.
          </p>
          <Button className="mt-3" block variant="secondary" onClick={() => setConsentOpen(true)}>
            Посмотреть и решить
          </Button>
        </Card>
      )}

      {plan.data && <MorningPlan plan={plan.data} familyId={familyId} />}

      <div data-coach="nanny_shift">
        <NannyShiftCard
          shift={activeShift}
          minutesSoFar={data.minutes_so_far}
          amountSoFar={data.amount_so_far}
          currency={currency}
          timeZone={timeZone}
          starting={startShift.isPending}
          onStart={async () => {
            const geo = await captureGeo()
            startShift.mutate(geo)
          }}
          onFinish={() => setFinishOpen(true)}
        />
      </div>

      <div data-coach="nanny_tiles">
        <SectionTitle>Отметить</SectionTitle>
        <EventTiles
          types={types.data ?? []}
          running={data.active_intervals}
          busyTypeId={busyTypeId}
          onCreate={onCreate}
          onStop={(event) => stopInterval.mutate(event.id)}
          extra={
            <>
              <ActionTile
                icon="exp-receipt"
                title="Расход"
                tone="warm"
                onPress={() => setExpenseOpen(true)}
              />
              {geoActive && (
                <ActionTile
                  // не метка на карте, а действие «я пришла» — отсюда ev-here
                  icon="ev-here"
                  title="Я на месте"
                  onPress={async () => {
                    const geo = await captureGeo()
                    if (!geo) {
                      toast.show('Не удалось получить геометку')
                      return
                    }
                    here.mutate(geo)
                  }}
                />
              )}
            </>
          }
        />
      </div>

      {plan.data && plan.data.tasks.some((task) => task.status === 'planned') && (
        <div>
          <SectionTitle>Задачи на сегодня</SectionTitle>
          <TaskList
            tasks={plan.data.tasks.filter((task) => task.status === 'planned')}
            canComplete
            busyId={completeTask.isPending ? completeTask.variables : null}
            onComplete={(task) => completeTask.mutate(task.id)}
            onSkip={setSkipping}
          />
        </div>
      )}

      <div>
        <SectionTitle>Сегодня</SectionTitle>
        <Timeline
          events={data.events}
          expenses={data.expenses}
          timeZone={timeZone}
          currency={currency}
          emptyTitle="День ещё не начался"
          emptyDescription="Отметьте первое событие — родители увидят его сразу."
          onPhotoPress={(urls, index) => setPhotos({ urls, index })}
          onGeoPress={setMapGeo}
        />
      </div>

      <ExpenseSheet
        open={expenseOpen}
        familyId={familyId}
        currency={currency}
        onClose={() => setExpenseOpen(false)}
      />
      <GeoConsentSheet
        open={consentOpen}
        familyId={familyId}
        onClose={() => setConsentOpen(false)}
      />
      <ConfirmSheet
        open={finishOpen}
        title="Завершить смену?"
        description="Часы и сумма зафиксируются. Если время нужно поправить, это можно сделать потом — с указанием причины."
        confirmLabel="Завершить"
        loading={finishShift.isPending}
        onConfirm={async () => {
          if (!activeShift) return
          const geo = await captureGeo()
          finishShift.mutate({ shiftId: activeShift.id, geo })
        }}
        onClose={() => setFinishOpen(false)}
      />
      {summary.data && !summary.data.shift_open && (
        <DaySummaryCard summary={summary.data} currency={currency} />
      )}

      <SkipSheet task={skipping} familyId={familyId} onClose={() => setSkipping(null)} />
      <MapSheet geo={mapGeo} timeZone={timeZone} onClose={() => setMapGeo(null)} />
      <PhotoViewer
        urls={photos?.urls ?? null}
        startIndex={photos?.index ?? 0}
        onClose={() => setPhotos(null)}
      />
    </Screen>
  )
}
